import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Market } from "./MarketCard";

type Cat = { slug: string; name: string; icon: string; color: string; market_count: number };

const SORTS = [
  { id: "trending", label: "Trending" },
  { id: "new", label: "Newest" },
  { id: "closing", label: "Closing" },
  { id: "movers", label: "Movers" },
];

function shortQuestion(q: string): string {
  return q.length > 42 ? `${q.slice(0, 40)}…` : q;
}

export function BrowseRail({ markets }: { markets: Market[] }) {
  const cats = useQuery({
    queryKey: ["cats"],
    queryFn: () => api<{ categories: Cat[] }>("/markets/categories"),
  });
  const movers = [...markets]
    .sort((a, b) => Math.abs(Number(b.change_24h_bps || 0)) - Math.abs(Number(a.change_24h_bps || 0)))
    .slice(0, 10);
  const closing = [...markets].sort((a, b) => a.timeRemaining.localeCompare(b.timeRemaining)).slice(0, 10);
  const useMovers = movers.some((m) => Number(m.change_24h_bps || 0) !== 0);
  const strip = useMovers ? movers : closing;

  return (
    <section className="browse-rail" aria-label="Browse markets">
      <div className="browse-rail-inner">
        <div className="browse-row">
          <span className="browse-label">Browse</span>
          <div className="browse-scroller">
            <Link to="/markets" className="browse-chip">All</Link>
            {SORTS.map((s) => (
              <Link key={s.id} to={`/markets?sort=${s.id}`} className="browse-chip">
                {s.label}
              </Link>
            ))}
            {(cats.data?.categories ?? []).map((c) => (
              <Link key={c.slug} to={`/markets?category=${c.slug}`} className="browse-chip">
                <span aria-hidden>{c.icon}</span>
                {c.name}
                <em>{c.market_count}</em>
              </Link>
            ))}
          </div>
        </div>
        {strip.length > 0 && (
          <div className="browse-row">
            <span className="browse-label">{useMovers ? "Biggest moves" : "Closing soon"}</span>
            <div className="browse-scroller">
              {strip.map((m) => {
                const chg = Number(m.change_24h_bps || 0) / 100;
                return (
                  <Link key={m.id} to={`/markets/${m.slug}`} className="mover-chip">
                    <span className="mover-q">{shortQuestion(m.question)}</span>
                    <strong>{m.yes_price_cents}%</strong>
                    {useMovers ? (
                      <span className={chg >= 0 ? "up" : "down"}>
                        {chg >= 0 ? "+" : ""}{chg.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="mover-time">{m.timeRemaining}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
